import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

/**
 * Semantic tone of a single segment. Deliberately named after the Volta
 * semantic families (success / alert / destructive / neutral) rather than after
 * any one feature's vocabulary, so the same primitive can render an order's
 * match composition, a stock split, or an import outcome without knowing what
 * the numbers mean.
 *
 * Fills resolve from `$segment-fill-palette` in `styles/ui/_colors.scss`, which
 * aliases tokens that already exist — this primitive introduces no new colours.
 */
export type SegmentedBarTone = 'success' | 'alert' | 'destructive' | 'neutral';

export interface ISegmentedBarSegment {
  value: number;
  tone: SegmentedBarTone;
}

/** A segment paired with its resolved width, so the template stays logic-free. */
interface IRenderedSegment extends ISegmentedBarSegment {
  widthPercent: number;
}

/**
 * Proportional multi-segment bar — one contiguous pill split by value.
 *
 * The component is dumb on purpose: it takes raw counts and normalises them
 * itself, so callers never pre-compute percentages (and can't drift from the
 * legend rendered beside the bar). Zero-valued segments are dropped rather than
 * rendered at 0% width, which would otherwise show as a hairline seam.
 */
@Component({
  selector: 'ui-segmented-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './segmented-bar.component.html',
  styleUrls: ['./segmented-bar.component.scss'],
})
export class SegmentedBarComponent {
  @Input() set segments(value: ISegmentedBarSegment[] | null | undefined) {
    this.source = value ?? [];
  }

  /** Bar thickness. `slim` suits inline table cells, `regular` popovers/cards. */
  @Input() size: 'slim' | 'regular' = 'regular';

  /** Screen-reader summary; the bar itself is decorative next to its legend. */
  @Input() ariaLabel = '';

  private source: ISegmentedBarSegment[] = [];

  get total(): number {
    return this.source.reduce((sum, segment) => sum + Math.max(0, segment.value || 0), 0);
  }

  /**
   * Widths are derived from the running total rather than rounded per segment,
   * so they always add up to exactly 100% — rounding each one independently
   * leaves a visible gap at the end of the bar on totals like 3 or 7.
   */
  get rendered(): IRenderedSegment[] {
    const total = this.total;
    if (!total) return [];

    let consumed = 0;
    let emitted = 0;
    const positive = this.source.filter((segment) => (segment.value || 0) > 0);

    return positive.map((segment, index) => {
      const isLast = index === positive.length - 1;
      consumed += segment.value;
      const widthPercent = isLast ? 100 - emitted : Math.round((consumed / total) * 100) - emitted;
      emitted += widthPercent;
      return { ...segment, widthPercent };
    });
  }
}
